import type { Language } from "@/lib/translations";
import { HOME_SHOWCASE_LIGHT_AVAILABLE } from "@/lib/home-showcase-light-manifest";

export type HomeShowcaseResolvedTheme = "light" | "dark";

/** Locale-safe screenshot suffix; invalid values fall back to Spanish. */
export function resolveHomeScreenshotLanguage(lang: unknown): Language {
  if (lang === "es" || lang === "en" || lang === "ca") return lang;
  return "es";
}

function scanDarkPath(name: string, lang: Language): string {
  return `/images/scan/${name}-${lang}.jpg`;
}

function scanLightPath(name: string, lang: Language): string {
  return `/images/scan/${name}-light-${lang}.jpg`;
}

/** Theme/language-aware path for a scan screenshot family (e.g. menu, map). */
export function getScanShowcaseImagePath(
  name: string,
  lang: Language,
  theme: HomeShowcaseResolvedTheme = "dark",
): string {
  const l = resolveHomeScreenshotLanguage(lang);
  const dark = scanDarkPath(name, l);
  if (theme !== "light") return dark;

  const lightKey = `${name}-light-${l}`;
  return HOME_SHOWCASE_LIGHT_AVAILABLE.has(lightKey) ? scanLightPath(name, l) : dark;
}

/**
 * Marketing hero showcase screenshots.
 * Dark mode always uses {name}-{lang}.jpg.
 * Light mode uses {name}-light-{lang}.jpg when that file is listed in
 * home-showcase-light-manifest.ts (regenerated from public/images/scan on build).
 */
export function getHomeShowcaseImagePaths(
  lang: Language,
  theme: HomeShowcaseResolvedTheme = "dark",
) {
  const l = resolveHomeScreenshotLanguage(lang);

  return {
    /** Find today's menu — map explore */
    findMenu: getScanShowcaseImagePath("map", l, theme),
    /** Real menu photos — menu detail */
    menuPhotos: getScanShowcaseImagePath("menu", l, theme),
    /** Menu alerts — notifications / alerts screen */
    alerts: getScanShowcaseImagePath("alerts", l, theme),
    /** Share lists — shared list flow */
    shareLists: getScanShowcaseImagePath("share", l, theme),
  } as const;
}
